import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { defaultLanguageTypes } from 'types/Asset';
import { getVodPlayerURL } from 'utils/fnUrl';
import {
    BitrateTrack,
    PlayerError,
    PlayerEvents,
    PlayerProps,
    PlayingAsset,
    CatchupPlayerAssetInfo,
    PlayingMode,
    SUBTITLE_NONE,
    Track,
    RecordingPlayerAssetInfo,
} from 'types/Player';
import { getLocalStorage, setLocalStorage, guardedSessionStorage } from 'utils/fnStorage';
import { compareTracks, selectDefaultAudio, selectDefaultSubtitles } from 'utils/fnData';
import Events from 'utils/Events';
import { ControlGroup } from 'components/Player/PlayerWrapper';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { isCatchupPlayerAsset, isLivePlayerAsset, isRecordingPlayerAsset, isVodPlayerAsset } from 'utils/fnTypeGuards';
import { usePlayback } from 'providers/usePlayback/PlaybackContext';
import { MILLISECONDS } from 'utils/TimeUnit';
import { YouboraOptions } from 'types/Tracking';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import { FetchParamById } from 'types/ApiTypes';
import { isSafari } from 'utils/fnDevices';
import { useCast } from 'providers/cast/CastContext';
import ApplicationConfig from 'providers/useConfig/ApplicationConfig';
import { useAssetReplayCapable } from 'hooks/useAssetReplayCapable/useAssetReplayCapable';
import { useVodWatchable } from 'hooks/useVodWatchable';
import GSApi from 'api/Api';
import { queryClient } from 'providers/QueryProvider';
import useYouboraContentTracking from 'hooks/useYouboraContentTracking';
import YouboraTracking from './YouboraTracking';
import PlaybackManager from './PlaybackManager';
import { PlayerProxy } from './PlayerProxy';
import {
    clearSessionLanguages,
    getSessionAudioLanguage,
    getSessionSubtitlesLanguage,
    setSessionAudioLanguage,
    setSessionSubtitlesLanguage,
} from './sessionLanguages';
import {
    STREAM_ERRORS,
    CONTENT_UNAVAILABLE,
    HEARING_IMPAIRED_ROLES,
    NETWORK_ERROR_CODE,
    STREAM_REPLACED,
    VISUALLY_IMPAIRED_ROLES,
} from './playerConstants';
import { IPlayer } from './abstract/IPlayer';

const VOLUME_LOCAL_STORAGE = 'playerVolume';
const VOLUME_STEP = 0.1;
const CAST_TIMEOUT_AFTER_OPERATION = 8 * 1000;
const BINGE_JUST_LOADED_KEY = 'bingeJustLoadedNextEpisode';
const BOOKMARK_MAX_CONSECUTIVE_FAILURES = 2;
const MIN_SAVEABLE_BOOKMARK_POSITION = 1;

type ResolvedPlaybackPosition = {
    id: string;
    position: number;
    isVod: boolean;
};

const buildPlaybackPositionKey = (resolved: ResolvedPlaybackPosition, position: number) =>
    `${resolved.isVod ? 'vod' : 'listing'}:${resolved.id}:${position}`;

const useThumbnail = () => {
    const [showThumbnail, setShowThumbnail] = useState<boolean>(false);
    const { config } = useConfig();

    const checkForThumbnails = useCallback(
        (engine: IPlayer) => {
            const isThumbnailActive = config?.isFeatureActive('thumbnail_scroll');
            const imageTrack = engine.getThumbnailTrack();
            setShowThumbnail(imageTrack != null && isThumbnailActive);
        },
        [config]
    );

    const getThumbnail = async (time: number) => PlaybackManager.getThumbnail(time);

    return {
        showThumbnail,
        checkForThumbnails,
        getThumbnail,
    };
};

// Invalidate the continue-watching cache so any mounted consumer (home stripe, collection
// page) refetches with its own scope after the playback position changes.
const updateContinueWatching = () => queryClient.invalidateQueries({ queryKey: ['continueWatching'] });

const usePlayerBookmark = (asset: PlayingAsset, playingMode: PlayingMode) => {
    const assetRef = useRef(asset);
    const playingModeRef = useRef(playingMode);
    const lastSavedKeyRef = useRef<string | null>(null);
    const { savePlaybackPosition } = usePlayback();
    const remainingTimeThreshold = ApplicationConfig?.app_config?.player_settings.bookmark_remaining_time_threshold_ms ?? 15;

    useEffect(() => {
        assetRef.current = asset;
    }, [asset]);

    useEffect(() => {
        playingModeRef.current = playingMode;
    }, [playingMode]);

    useEffect(() => {
        lastSavedKeyRef.current = null;
    }, [asset?.id]);

    const resolvePlaybackPosition = useCallback((isOnline: boolean = true): ResolvedPlaybackPosition | null => {
        if (
            !assetRef.current ||
            !(
                isCatchupPlayerAsset(assetRef.current) ||
                isRecordingPlayerAsset(assetRef.current) ||
                (isLivePlayerAsset(assetRef.current) && playingModeRef.current === PlayingMode.TIME_SHIFT) ||
                (isVodPlayerAsset(assetRef.current) && assetRef.current.id)
            ) ||
            !PlaybackManager.isPlayerLoaded()
        ) {
            return null;
        }

        let position = PlaybackManager.getProgress() - PlaybackManager.getPrePadding();
        const remainingTime = Math.floor(PlaybackManager.getDuration()) - Math.floor(PlaybackManager.getProgress());

        if ((isOnline && isCatchupPlayerAsset(assetRef.current)) || isRecordingPlayerAsset(assetRef.current)) {
            const duration = MILLISECONDS.toSeconds(assetRef.current.endTime - assetRef.current.startTime);
            position = Math.max(0, Math.min(position, duration));
        }

        if (isCatchupPlayerAsset(assetRef.current)) {
            const catchupAsset = assetRef.current as CatchupPlayerAssetInfo;
            return { id: catchupAsset.listing_id || catchupAsset.listingId, position, isVod: false };
        }

        if (isRecordingPlayerAsset(assetRef.current)) {
            const recordingAsset = assetRef.current as RecordingPlayerAssetInfo;
            return { id: recordingAsset.listingId, position, isVod: false };
        }

        if (isVodPlayerAsset(assetRef.current)) {
            return { id: assetRef.current.id, position, isVod: true };
        }

        if (remainingTime <= remainingTimeThreshold) {
            return { id: assetRef.current.listingId, position: 0, isVod: false };
        }

        return null;
    }, []);

    const savePlaybackPositionPosition = useCallback(
        async (isOnline: boolean = true): Promise<boolean | null> => {
            if (!isOnline) {
                return null;
            }

            const resolved = resolvePlaybackPosition(isOnline);

            if (!resolved) {
                return null;
            }

            const position = Math.floor(resolved.position);
            const key = buildPlaybackPositionKey(resolved, position);

            if (position < MIN_SAVEABLE_BOOKMARK_POSITION || key === lastSavedKeyRef.current) {
                return null;
            }

            lastSavedKeyRef.current = key;

            const saved = await savePlaybackPosition(resolved.id, position, resolved.isVod);

            if (!saved) {
                lastSavedKeyRef.current = null;
            }

            updateContinueWatching();

            return saved;
        },
        [savePlaybackPosition, resolvePlaybackPosition]
    );

    const savePlaybackPositionOnUnload = useCallback((): void => {
        const resolved = resolvePlaybackPosition(true);

        if (!resolved) {
            return;
        }

        const position = Math.floor(resolved.position);
        const key = buildPlaybackPositionKey(resolved, position);

        if (position < MIN_SAVEABLE_BOOKMARK_POSITION || key === lastSavedKeyRef.current) {
            return;
        }

        lastSavedKeyRef.current = key;

        GSApi.sendPlaybackPositionKeepalive(resolved.id, position, resolved.isVod);
    }, [resolvePlaybackPosition]);

    return {
        savePlaybackPositionPosition,
        savePlaybackPositionOnUnload,
    };
};

const useMiniPlayer = (savePlaybackPositionPosition: (isOnline?: boolean) => Promise<boolean | null>) => {
    const [miniPlayerProps, setMiniPlayerProps] = useState<PlayerProps>(null);
    const [isMini, setIsMini] = useState<boolean>(false);

    const switchToMiniPlayer = (props: PlayerProps) => {
        savePlaybackPositionPosition(true);
        setMiniPlayerProps(props);
        setIsMini(true);
    };

    const updateMiniPlayerProps = (props: PlayerProps) => {
        setMiniPlayerProps(props);
    };

    const closeMiniPlayer = () => {
        setMiniPlayerProps(null);
        setIsMini(false);
    };

    return {
        miniPlayerProps,
        isMini,
        switchToMiniPlayer,
        updateMiniPlayerProps,
        closeMiniPlayer,
    };
};

const useBingeWatching = (asset: PlayingAsset, config: typeof ApplicationConfig, isMini: boolean, dismissCallback) => {
    const [nextEpisode, setNextEpisode] = useState<any | null>(null);
    const [nextEpisodeWatchable, setNextEpisodeWatchable] = useState<boolean>(false);
    const [displayBingeWatch, setDisplayBingeWatch] = useState<boolean>(false);
    const [bingeWatchDuration, setBingeWatchDuration] = useState<any | null>(null);
    const [bingeWatchDismissed, setBingeWatchDismissed] = useState<boolean>(false);
    const [showNextEpisode, setShowNextEpisode] = useState<boolean>(false);

    const isMountedRef = useRef(true);
    const dismissed = useRef<boolean>(false);
    const display = useRef<boolean>(false);
    const justLoadedNewEpisode = useRef<boolean>(guardedSessionStorage.getItem(BINGE_JUST_LOADED_KEY) === 'true');
    const loadedEpisodeId = useRef<string>(null);

    const nextEpisodeRef = useRef(null);
    const nextEpisodeWatchableRef = useRef<boolean>(false);
    const assetRef = useRef<PlayingAsset>(null);

    const navigate = useNavigate();

    const isVodEpisode = !!isVodPlayerAsset(asset) && !asset.isMovie;
    const { isVodEpisodeWatchable } = useVodWatchable(isVodEpisode);

    useEffect(
        () => () => {
            isMountedRef.current = false;
        },
        []
    );

    const { response: series, fetcher: fetchEpisodeDetails } = useDataFetcher<any, FetchParamById>((params) =>
        GSApi.fetchVodSeriesEpisodes(params.id, (res) => res)
    );

    const showBingeWatchingBanner = useCallback(() => {
        const next = nextEpisodeRef.current;
        const refAsset = assetRef.current;

        if (!isVodPlayerAsset(refAsset) || !next) {
            return;
        }

        if (isVodPlayerAsset(refAsset) && refAsset.isMovie) {
            return;
        }

        if (!nextEpisodeWatchableRef.current) {
            return;
        }

        const bannerTimeout = MILLISECONDS.toSeconds(config.app_config.player_settings.binge_watching_start_timeout);
        let thresholdSeconds = (config.app_config.player_settings.binge_watching_threshold / 100) * PlaybackManager.getDuration();

        const remainingTime = Math.floor(PlaybackManager.getDuration()) - Math.floor(PlaybackManager.getProgress());

        if (thresholdSeconds < bannerTimeout + 2) {
            thresholdSeconds = bannerTimeout + 2;
        }

        if (justLoadedNewEpisode.current) {
            if (remainingTime > thresholdSeconds) {
                justLoadedNewEpisode.current = false;
                guardedSessionStorage.removeItem(BINGE_JUST_LOADED_KEY);
            }
            return;
        }

        if (remainingTime && thresholdSeconds && remainingTime < thresholdSeconds && !display.current && !dismissed.current) {
            if (isMountedRef.current) {
                setDisplayBingeWatch(true);
                setBingeWatchDuration(bannerTimeout);
            }
        }
    }, [config, asset]);

    const dismissBingeWatchingBanner = useCallback(
        (exitPlayer?: boolean) => {
            setBingeWatchDismissed(true);
            setDisplayBingeWatch(false);
            setShowNextEpisode(nextEpisode !== null);

            if (exitPlayer) {
                dismissCallback();
                navigate(-1);
            }
        },
        [dismissCallback, navigate]
    );

    const loadNextEpisode = useCallback(() => {
        if (!isMini && nextEpisode && nextEpisodeWatchable) {
            setDisplayBingeWatch(false);
            justLoadedNewEpisode.current = true;
            guardedSessionStorage.setItem(BINGE_JUST_LOADED_KEY, 'true');
            dismissCallback();
            guardedSessionStorage.setItem('returnPath', 'back');
            navigate(getVodPlayerURL(nextEpisode.id), { replace: true });
            window.location.reload();
        }
    }, [nextEpisode, nextEpisodeWatchable, isMini]);

    const fetchNextEpisodes = useCallback(() => {
        if (asset && isVodPlayerAsset(asset)) {
            const { isMovie, vodSeriesId } = asset;

            if (!isMovie) {
                fetchEpisodeDetails({ id: vodSeriesId });
            }
        }
    }, [asset]);

    useEffect(() => {
        const currentAssetId = asset?.id ?? null;

        if (currentAssetId !== loadedEpisodeId.current) {
            loadedEpisodeId.current = currentAssetId;

            setDisplayBingeWatch(false);
            setBingeWatchDismissed(false);
            setBingeWatchDuration(null);
            setNextEpisode(null);
            nextEpisodeRef.current = null;
        }

        assetRef.current = asset;
        fetchNextEpisodes();
    }, [asset]);

    useEffect(() => {
        if (series?.results && asset) {
            const sameSeasonNext = series.results.find(
                (ep) => ep.season_number === asset.seasonNumber && ep.episode_number === asset.episodeNumber + 1
            );
            const nextSeasonEpisode = series.results.find((ep) => ep.season_number === asset.seasonNumber + 1);

            const next = sameSeasonNext ?? nextSeasonEpisode;
            const watchable = isVodEpisodeWatchable(next);

            nextEpisodeRef.current = next;
            nextEpisodeWatchableRef.current = watchable;
            setNextEpisode(next);
            setNextEpisodeWatchable(watchable);
        }
    }, [series, isVodEpisodeWatchable]);

    useEffect(() => {
        display.current = displayBingeWatch;
        dismissed.current = bingeWatchDismissed;
    }, [displayBingeWatch, bingeWatchDismissed]);

    return {
        showBingeWatchingBanner,
        dismissBingeWatchingBanner,
        loadNextEpisode,
        nextEpisode,
        nextEpisodeWatchable,
        displayBingeWatch,
        bingeWatchDuration,
        setDisplayBingeWatch,
        showNextEpisode,
        setShowNextEpisode,
    };
};

const useVolume = (engine: IPlayer, castConnected: boolean) => {
    const volumeSetup = getLocalStorage(VOLUME_LOCAL_STORAGE);

    const [isMuted, setIsMuted] = useState<boolean>(volumeSetup?.muted ?? false);
    const [volume, setVolume] = useState<number>((volumeSetup?.level ?? 30) / 100);

    const volumeRef = useRef<number>(volume);
    const storedVolumeRef = useRef<number>(volume > 0 ? volume : 0.5);
    const isMutedRef = useRef<boolean>(isMuted);
    const setVolumeTimeout = useRef<number>(null);

    const getVolumeIcon = useCallback((level: number): 'mute' | 'volumeDown' | 'volumeUp' => {
        if (level === 0) return 'mute';
        if (level <= 0.5) return 'volumeDown';
        return 'volumeUp';
    }, []);

    const setVolumeLevel = useCallback(
        (level: number) => {
            volumeRef.current = level;
            if (engine) {
                engine.setVolume(level);
            }

            if (level > 0) {
                storedVolumeRef.current = level;
            }

            const muted = level === 0;
            setIsMuted(muted);
            isMutedRef.current = muted;

            setLocalStorage(VOLUME_LOCAL_STORAGE, {
                level: level * 100,
                muted,
            });

            setVolume(level);
        },
        [engine]
    );

    // for controlling the volume with arrow up & down buttons
    const adjustVolume = useCallback(
        (increment: boolean) => {
            const currentVolume = volumeRef.current;
            const newVolume = increment ? Math.min(1, currentVolume + VOLUME_STEP) : Math.max(0, currentVolume - VOLUME_STEP);

            setVolumeLevel(newVolume);

            if (newVolume === 0) {
                setIsMuted(true);
                isMutedRef.current = true;
                engine?.mute(true, true);
            } else if (isMutedRef.current) {
                setIsMuted(false);
                isMutedRef.current = false;
                engine?.mute(false, true);
            }

            return getVolumeIcon(newVolume);
        },
        [setVolumeLevel, engine, getVolumeIcon]
    );

    const toggleMute = useCallback(() => {
        setIsMuted((prevMuted) => {
            const newMutedState = !prevMuted;
            engine?.mute(newMutedState, true);

            if (newMutedState) {
                if (volumeRef.current > 0) {
                    storedVolumeRef.current = volumeRef.current;
                    setVolumeLevel(0);
                }
            } else {
                const volumeToSet = storedVolumeRef.current > 0 ? storedVolumeRef.current : 0.5;
                setVolumeLevel(volumeToSet);
            }

            isMutedRef.current = newMutedState;
            return newMutedState;
        });

        return isMutedRef.current ? 'mute' : getVolumeIcon(volumeRef.current);
    }, [engine, setVolumeLevel, getVolumeIcon]);

    const onVolumeStateChange = useCallback(() => {
        if (castConnected) {
            clearTimeout(setVolumeTimeout.current);
            setVolumeTimeout.current = window.setTimeout(() => {
                if (!engine || volumeRef.current === engine.getVolume()) {
                    return;
                }
                setVolumeLevel(engine.getVolume());
            }, 250);
        } else if (engine?.isMuted()) {
            setIsMuted(true);
            isMutedRef.current = true;
        }
    }, [castConnected, engine, setVolumeLevel]);

    const initVolumeLevel = useCallback(() => {
        if (!engine) {
            return;
        }

        if (volumeSetup) {
            engine.setVolume(volumeSetup.level / 100);
            engine.mute(volumeSetup.muted, false);
        } else {
            engine.setVolume(0.5);
        }
    }, [engine]);

    return {
        isMuted,
        isMutedRef,
        adjustVolume,
        VOLUME_STEP,
        volume,
        volumeRef,
        setVolumeLevel,
        toggleMute,
        onVolumeStateChange,
        initVolumeLevel,
        getVolumeIcon,
    };
};

const usePlayerService = () => {
    // storage values
    const appLanguageStorage = getLocalStorage('appLanguage');
    const primaryAudioLanguageStorage = getLocalStorage('primaryAudioLanguage');
    const primarySubtitlesLanguageStorage = getLocalStorage('primarySubtitlesLanguage');
    const accessibilitySoundStorage = getLocalStorage('accessibilitySound');
    const accessibilitySubsStorage = getLocalStorage('accessibilitySubs');

    // states
    const [playerEngine, setPlayerEngine] = useState<IPlayer | null>(null);
    const [asset, setAsset] = useState<PlayingAsset>();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [initialized, setInitialized] = useState<boolean>(true);
    const [loaded, setLoaded] = useState<boolean>(false);
    const [isBuffering, setIsBuffering] = useState<boolean>(false);
    const [initCasting, setInitCasting] = useState<boolean>(false);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [activeControlGroup, setActiveControlGroup] = useState<ControlGroup>(ControlGroup.BASE);
    const [playerError, setPlayerError] = useState<PlayerError | null>(null);
    const [hasMultipleTracks, setHasMultipleTracks] = useState<boolean>(false);
    const [streamEndReached, setStreamEndReached] = useState<boolean>(false);
    const [playingMode, setPlayingMode] = useState<PlayingMode>(PlayingMode.NORMAL);
    const [toJumpValue, setToJumpValue] = useState<number>(0);
    const [isEmbedded, setIsEmbedded] = useState<boolean>(false);

    const [audioTrack, setAudioTrack] = useState<Track | null>(null);
    const [subtitleTrack, setSubtitleTrack] = useState<Track | null>(null);
    const [bitrateTrack, setBitrateTrack] = useState<BitrateTrack>(null);

    const [primaryAudioLanguage, setPrimaryAudioLanguage] = useState<string>(primaryAudioLanguageStorage || appLanguageStorage || 'et');
    const [primarySubtitlesLanguage, setPrimarySubtitlesLanguage] = useState<string>(
        primarySubtitlesLanguageStorage || appLanguageStorage || 'et'
    );
    const [accessibilitySound, setAccessibilitySound] = useState<boolean>(accessibilitySoundStorage || false);
    const [accessibilitySubs, setAccessibilitySubs] = useState<boolean>(accessibilitySubsStorage || false);

    // refs
    const events = useRef<Events>(new Events());
    const engine = useRef<IPlayer | null>(null);
    const applyJumpTimeoutRef = useRef<number | null>(null);
    const toJumpValueRef = useRef<number | null>(0);
    const errorRef = useRef<PlayerError | undefined>(undefined);
    const lastCastEmitTimestampRef = useRef<number>(0);

    const audioTrackRef = useRef<Track | null>(null);
    const subtitleTrackRef = useRef<Track | null>(null);
    const subtitleInitialisedRef = useRef(false);
    const bitrateTrackRef = useRef<BitrateTrack>(null);

    const accessibilitySoundRef = useRef<boolean>(accessibilitySound);
    const accessibilitySubsRef = useRef<boolean>(accessibilitySubs);

    const selectedTrack = useRef<{ audio: Track; subtitle: Track; bitrate: BitrateTrack }>({
        audio: null,
        subtitle: null,
        bitrate: null,
    });

    const preferredAudioTrack = useRef<Track | null>(null);
    const preferredSubtitleTrack = useRef<Track | null>(null);
    const preferredBitrateTrack = useRef<BitrateTrack>(null);

    const castSubtitleIntentRef = useRef<Track | null>(null);
    const castConnectedRef = useRef<boolean>(false);

    const playAfterLoad = useRef<boolean>(true);
    const isPlayed = useRef<boolean>(true);
    const emitTimeShiftEndedEvent = useRef<boolean>(true);
    const seekTimeoutRef = useRef<number | null>(null);
    const savePlaybackPositionIntervalRef = useRef<number | null>(null);
    const bookmarkFailureCountRef = useRef<number>(0);
    const playbackEndSavedRef = useRef<boolean>(false);

    const { config } = useConfig();
    const { isReplayCapable } = useAssetReplayCapable(asset);
    const { castConnected } = useCast();
    const { showThumbnail, checkForThumbnails, getThumbnail } = useThumbnail();
    const { savePlaybackPositionPosition, savePlaybackPositionOnUnload } = usePlayerBookmark(asset, playingMode);
    const { isMini, miniPlayerProps, switchToMiniPlayer, updateMiniPlayerProps, closeMiniPlayer } =
        useMiniPlayer(savePlaybackPositionPosition);
    const navigate = useNavigate();

    const { trackPlayerInteraction, trackContentPlayFromAsset } = useYouboraContentTracking();

    const {
        showBingeWatchingBanner,
        dismissBingeWatchingBanner,
        loadNextEpisode,
        nextEpisode,
        nextEpisodeWatchable,
        displayBingeWatch,
        bingeWatchDuration,
        setDisplayBingeWatch,
        showNextEpisode,
        setShowNextEpisode,
    } = useBingeWatching(asset, config, isMini, () => {
        setStreamEndReached(true);
    });
    const { onVolumeStateChange, initVolumeLevel, setVolumeLevel, toggleMute, adjustVolume, volume, volumeRef, isMuted, isMutedRef } =
        useVolume(playerEngine, castConnected);

    const getProgress = (): number => PlaybackManager.getProgress();

    const setEngine = (toSetEngine: IPlayer) => {
        const proxyEngine = new PlayerProxy(toSetEngine);
        setPlayerEngine(proxyEngine);
    };

    const getSeekMax = (): number => PlaybackManager.getSeekMax();

    const getProgressWithJump = useCallback((): number => {
        if (!isLivePlayerAsset(asset)) {
            return getProgress() + toJumpValueRef.current;
        }

        const progress = getProgress();

        return progress + toJumpValueRef.current;
    }, [asset, playingMode]);

    const setPlayingState = useCallback(
        async (playing, timeShiftPlayed = false) => {
            if (playerError) return;

            isPlayed.current = playing;

            if (playing) {
                await PlaybackManager.setPlayingState(playing, timeShiftPlayed);

                if (asset?.id && asset.id.trim() !== '') {
                    trackPlayerInteraction('play', asset.id);
                }
            } else {
                await PlaybackManager.setPlayingState(playing);
                savePlaybackPositionPosition(true);

                if (asset?.id && asset.id.trim() !== '') {
                    trackPlayerInteraction('pause', asset.id);
                }
            }

            setIsPlaying(playing);
        },
        [asset, playerError, playingMode, trackPlayerInteraction]
    );

    const getPrePadding = (): number => PlaybackManager.getPrePadding();

    const seek = async (to: number) => {
        if (to && !playerError) await PlaybackManager.seek(to);
    };

    const seekStart = useCallback(() => {}, [playerError]);

    const seekEnd = useCallback(() => {
        if (playerError !== null && playerError !== undefined) return;

        if (asset?.id && asset.id.trim() !== '') {
            trackPlayerInteraction('seek', asset.id);
        }
    }, [playerError, trackPlayerInteraction, asset]);

    const jumpToLive = useCallback(async () => {
        if (isCatchupPlayerAsset(asset)) {
            events.current.triggerEvents(PlayerEvents.JUMP_TO_LIVE, null);
        } else {
            await PlaybackManager.switchToNormalMode();
        }
        setPlayingState(true);
    }, [asset]);

    const setBufferingState = useCallback(
        (event) => {
            setIsBuffering(event.buffering);
        },
        [setIsBuffering]
    );

    const onWaiting = useCallback(() => {
        if (!isSafari()) {
            setIsBuffering(true);
        }
    }, [setIsBuffering]);

    const playStateChanged = useCallback(
        (e) => {
            if (!castConnected) {
                setIsBuffering(false);
            }
            if (e && (e.type === 'play' || e.type === 'pause')) {
                setIsPlaying(e.type === 'play');
                setStreamEndReached(false);
            }
        },
        [setIsBuffering, setIsPlaying, castConnected]
    );

    const endOfStreamReached = useCallback(() => {
        setIsBuffering(false);

        events.current.triggerEvents(PlayerEvents.END_OF_STREAM, null);
        setStreamEndReached(true);
    }, []);

    const onlineStateChanged = useCallback(() => {
        if (playerError && playerError.code === NETWORK_ERROR_CODE) {
            setPlayerError(null);

            // if there was a network error and the current asset is live seek to current live position
            if (isLivePlayerAsset(asset) && playerEngine && playingMode === PlayingMode.NORMAL) {
                playerEngine.seekTo(0);
            }
        }
    }, [asset, playingMode, playerEngine, playerError]);

    const getAudioLanguages = (): Track[] => engine.current?.getAudioLanguages() ?? [];

    const getTextLanguages = (): Track[] => engine.current?.getTextLanguages() ?? [];

    const getBitrateTracks = (): BitrateTrack[] => engine.current?.getBitrateTracks() ?? [];

    const getPreferredBitrateTrack = (): any => engine.current?.getSelectedBitrateTrack() ?? preferredBitrateTrack.current;

    const getPreferredAudio = (): any => engine.current?.getSelectedLanguage() ?? preferredAudioTrack.current;

    const getPreferredSubtitle = (): any => engine.current?.getSelectedSubtitle() ?? preferredSubtitleTrack.current;

    const setAudioLanguage = (audio: Track) => {
        selectedTrack.current.audio = audio;
        setAudioTrack(audio);
    };

    const enforceCastSubtitle = (subtitleTracks: Track[]) => {
        if (!castConnectedRef.current || !subtitleTracks?.length) {
            return;
        }

        const wanted = castSubtitleIntentRef.current;

        if (!wanted) {
            return;
        }

        const receiverSelected = engine.current?.getSelectedSubtitle();
        const activeLanguage = receiverSelected?.language ?? null;
        const wantedIsOff = wanted.language === SUBTITLE_NONE;

        if (wanted.language === activeLanguage) {
            return;
        }

        const target = wantedIsOff ? wanted : subtitleTracks.find((track) => track.language === wanted.language);

        if (target) {
            engine.current?.setTextLanguage(target);
            setSubtitleTrack(target);
        }
    };

    const initAudioSubtitle = () => {
        if (engine.current.getAudioLanguages().length) {
            const preferred = getPreferredAudio();
            const audioTracks = getAudioLanguages();

            if (!audioTrackRef.current) {
                const preferredLanguage = getSessionAudioLanguage() || getLocalStorage('primaryAudioLanguage') || primaryAudioLanguage;
                const byPreference = selectDefaultAudio(
                    { primaryAudioLanguage: preferredLanguage },
                    audioTracks,
                    accessibilitySoundRef.current,
                    VISUALLY_IMPAIRED_ROLES
                );
                setAudioLanguage(byPreference ?? audioTracks[0]);
            } else if (preferred) {
                if (audioTrackRef.current?.language !== preferred?.language || audioTrackRef.current?.role !== preferred?.role) {
                    setAudioTrack(preferred);
                }
            } else if (!audioTracks.some((track) => compareTracks(track, audioTrackRef.current))) {
                const preferredLanguage = getSessionAudioLanguage() || getLocalStorage('primaryAudioLanguage') || primaryAudioLanguage;

                setAudioLanguage(
                    selectDefaultAudio(
                        { primaryAudioLanguage: preferredLanguage },
                        audioTracks,
                        accessibilitySoundRef.current,
                        VISUALLY_IMPAIRED_ROLES
                    )
                );
            }
            preferredAudioTrack.current = null;
        }

        if (engine.current.getTextLanguages().length) {
            const preferred = getPreferredSubtitle();
            const subtitleTracks = getTextLanguages();

            if (!subtitleTrackRef.current || !subtitleInitialisedRef.current) {
                const preferredLanguage =
                    getSessionSubtitlesLanguage() || getLocalStorage('primarySubtitlesLanguage') || primarySubtitlesLanguage;
                const byPreference = selectDefaultSubtitles(
                    { primarySubtitlesLanguage: preferredLanguage },
                    subtitleTracks,
                    accessibilitySubsRef.current,
                    HEARING_IMPAIRED_ROLES
                );
                // item 0 is always NONE
                const activeSubtitle = subtitleTracks.find((subtitle) => subtitle.raw?.active === true);
                setSubtitleTrack(byPreference ?? activeSubtitle ?? subtitleTracks[0]);
                subtitleInitialisedRef.current = subtitleTracks.length > 1;
            } else if (preferred) {
                const intent = castSubtitleIntentRef.current;
                const receiverDisagreesWithIntent = castConnectedRef.current && intent != null && preferred.language !== intent.language;

                if (
                    !receiverDisagreesWithIntent &&
                    (subtitleTrackRef.current?.language !== preferred?.language || subtitleTrackRef.current?.role !== preferred?.role)
                ) {
                    setSubtitleTrack(preferred);
                }
            }
            preferredSubtitleTrack.current = null;

            enforceCastSubtitle(subtitleTracks);
        }

        if (engine.current.getBitrateTracks()?.length) {
            const preferred = getPreferredBitrateTrack();
            if (preferred) {
                if (bitrateTrackRef.current?.height !== preferred?.height) {
                    setBitrateTrack(preferred);
                }
                preferredBitrateTrack.current = null;
            } else {
                const bitrateTracks = getBitrateTracks();
                let activeBitrateTrack: BitrateTrack;

                // use first available bitrate track
                if (!bitrateTrackRef.current && bitrateTracks.length) {
                    activeBitrateTrack = bitrateTracks.find((bitrate) => bitrate.raw?.active === true);
                }
                setBitrateTrack(activeBitrateTrack || bitrateTracks[0]);
            }
        }
    };

    const onTracksChanged = useCallback(() => {
        initAudioSubtitle();
        setHasMultipleTracks(Object.keys(getAudioLanguages()).length > 1 || Object.keys(getTextLanguages()).length > 1);
        checkForThumbnails(engine.current);
    }, [setHasMultipleTracks, checkForThumbnails]);

    const getDuration = (): number => PlaybackManager.getDuration();

    const getPostPadding = (): number => PlaybackManager.getPostPadding();

    const getSecondaryProgress = (): number => PlaybackManager.getSecondaryProgress();

    const getSecondaryProgressWithJump = useCallback(
        (): number => getSecondaryProgress() + (isLivePlayerAsset(asset) ? 0 : toJumpValueRef.current),
        [asset]
    );

    const canUpdateProgress = (): boolean => true;

    const timeUpdatedListener = useCallback(() => {
        if (castConnected) {
            if (lastCastEmitTimestampRef.current) {
                if (Date.now() < lastCastEmitTimestampRef.current + CAST_TIMEOUT_AFTER_OPERATION) {
                    return;
                }
            }
        }

        events.current.triggerEvents(PlayerEvents.TIME_UPDATE, null);

        showBingeWatchingBanner();
    }, [castConnected, showBingeWatchingBanner]);

    const setTextLanguage = (subtitle: Track) => {
        castSubtitleIntentRef.current = subtitle;
        selectedTrack.current.subtitle = subtitle;
        setSubtitleTrack(subtitle);
    };

    const setBitrateStream = (bitrate: BitrateTrack) => {
        selectedTrack.current.bitrate = bitrate;
        setBitrateTrack(bitrate);
    };

    const jump = (value: number) => {
        const currentProgress = getProgress();
        const seekMax = getSeekMax();

        const finalJumpValue = Math.max(Math.min(value, seekMax - currentProgress), -currentProgress);

        const jumpFn = async () => {
            const newPosition = currentProgress + finalJumpValue;

            // 0.5 is because it wont seek to beginning of the stream if the newPosition equals 0
            await seek(Math.max(Math.min(newPosition, seekMax), 0.5));

            setToJumpValue(0);
        };

        return jumpFn().catch(() => {});
    };

    const attachPlayerListeners = useCallback(() => {
        if (playerEngine) {
            playerEngine.addEventListener(PlayerEvents.BUFFERING, setBufferingState);
            playerEngine.addEventListener(PlayerEvents.WAITING, onWaiting);
            playerEngine.addEventListener(PlayerEvents.VOLUME_CHANGE, onVolumeStateChange);
            playerEngine.addEventListener(PlayerEvents.PLAY, playStateChanged);
            playerEngine.addEventListener(PlayerEvents.BUFFER_END, playStateChanged);
            playerEngine.addEventListener(PlayerEvents.PLAYING, playStateChanged);
            playerEngine.addEventListener(PlayerEvents.PAUSE, playStateChanged);
            playerEngine.addEventListener(PlayerEvents.ENDED, endOfStreamReached);
            playerEngine.addEventListener(PlayerEvents.TRACKS_CHANGED, onTracksChanged);
            playerEngine.addEventListener(PlayerEvents.TIME_UPDATE, timeUpdatedListener);
        }
    }, [
        playerEngine,
        setBufferingState,
        onWaiting,
        onVolumeStateChange,
        playStateChanged,
        endOfStreamReached,
        onTracksChanged,
        timeUpdatedListener,
    ]);

    const detachPlayerListeners = useCallback(() => {
        if (playerEngine) {
            playerEngine.removeEventListener(PlayerEvents.BUFFERING, setBufferingState);
            playerEngine.removeEventListener(PlayerEvents.WAITING, onWaiting);
            playerEngine.removeEventListener(PlayerEvents.VOLUME_CHANGE, onVolumeStateChange);
            playerEngine.removeEventListener(PlayerEvents.PLAY, playStateChanged);
            playerEngine.removeEventListener(PlayerEvents.BUFFER_END, playStateChanged);
            playerEngine.removeEventListener(PlayerEvents.PLAYING, playStateChanged);
            playerEngine.removeEventListener(PlayerEvents.PAUSE, playStateChanged);
            playerEngine.removeEventListener(PlayerEvents.ENDED, endOfStreamReached);
            playerEngine.removeEventListener(PlayerEvents.TRACKS_CHANGED, onTracksChanged);
            playerEngine.removeEventListener(PlayerEvents.TIME_UPDATE, timeUpdatedListener);
        }
    }, [
        playerEngine,
        setBufferingState,
        onWaiting,
        onVolumeStateChange,
        playStateChanged,
        endOfStreamReached,
        onTracksChanged,
        timeUpdatedListener,
    ]);

    const release = async () => {
        clearSessionLanguages();
        savePlaybackPositionPosition(errorRef.current == null || errorRef.current?.code !== NETWORK_ERROR_CODE);

        PlaybackManager.setPlayingMode(PlayingMode.NORMAL);

        if (engine.current) {
            detachPlayerListeners();
        }

        PlaybackManager.destroy();

        setPlayerEngine(null);
        setAsset(null);
        setHasMultipleTracks(false);
        setPlayerError(null);
        setLoaded(false);
        setIsPlaying(false);
        playAfterLoad.current = true;

        emitTimeShiftEndedEvent.current = true;
        setAudioTrack(null);
        setSubtitleTrack(null);

        audioTrackRef.current = null;
        subtitleTrackRef.current = null;
        subtitleInitialisedRef.current = false;

        preferredAudioTrack.current = null;
        preferredSubtitleTrack.current = null;
        preferredBitrateTrack.current = null;

        if (applyJumpTimeoutRef.current) {
            clearTimeout(applyJumpTimeoutRef.current);
        }

        if (seekTimeoutRef.current) {
            clearTimeout(seekTimeoutRef.current);
        }
    };

    const resetPlayerAsset = (playMode?: PlayingMode) => {
        setPlayerError(null);
        setAsset(null);
        setIsBuffering(false);

        setPlayingMode(playMode ?? PlayingMode.NORMAL);
        PlaybackManager.setPlayingMode(playMode ?? PlayingMode.NORMAL);

        emitTimeShiftEndedEvent.current = true;

        setAudioTrack(null);
        setSubtitleTrack(null);
        audioTrackRef.current = null;
        subtitleTrackRef.current = null;
        subtitleInitialisedRef.current = false;

        preferredAudioTrack.current = null;
        preferredSubtitleTrack.current = null;
        preferredBitrateTrack.current = null;

        if (seekTimeoutRef.current) {
            clearTimeout(seekTimeoutRef.current);
        }
    };

    const addEventListener = (type: PlayerEvents, callback: () => void) => {
        events.current.addEventListener(type, callback);
    };

    const removeEventListener = (type: PlayerEvents, callback: () => void) => {
        events.current.removeEventListener(type, callback);
    };

    const resetError = () => {
        setPlayerError(null);
    };

    const changeDefaultAudioLanguage = (type: defaultLanguageTypes, toSetLanguage: string) => {
        if (type === defaultLanguageTypes.PRIMARY) {
            setSessionAudioLanguage(null);
            setPrimaryAudioLanguage(toSetLanguage);
        }
    };

    const changeDefaultSubtitlesLanguage = (type: defaultLanguageTypes, toSetLanguage: string) => {
        if (type === defaultLanguageTypes.PRIMARY) {
            setSessionSubtitlesLanguage(null);
            setPrimarySubtitlesLanguage(toSetLanguage);
        }
    };

    useEffect(() => {
        setLocalStorage('primaryAudioLanguage', primaryAudioLanguage);
    }, [primaryAudioLanguage]);

    useEffect(() => {
        setLocalStorage('primarySubtitlesLanguage', primarySubtitlesLanguage);
    }, [primarySubtitlesLanguage]);

    useEffect(() => {
        accessibilitySoundRef.current = accessibilitySound;
        setLocalStorage('accessibilitySound', accessibilitySound);
    }, [accessibilitySound]);

    useEffect(() => {
        accessibilitySubsRef.current = accessibilitySubs;
        setLocalStorage('accessibilitySubs', accessibilitySubs);
    }, [accessibilitySubs]);

    useEffect(() => {
        if (isPlaying && asset?.id && asset.id.trim() !== '') {
            trackContentPlayFromAsset(asset);
        }
    }, [isBuffering, isPlaying, asset?.id, trackContentPlayFromAsset]);

    // Crash/force-quit safety net. Trips a breaker after repeated failures so it cannot loop.
    useEffect(() => {
        if (isPlaying && !savePlaybackPositionIntervalRef.current) {
            savePlaybackPositionIntervalRef.current = window.setInterval(async () => {
                if (bookmarkFailureCountRef.current >= BOOKMARK_MAX_CONSECUTIVE_FAILURES) {
                    clearInterval(savePlaybackPositionIntervalRef.current);
                    savePlaybackPositionIntervalRef.current = null;
                    return;
                }

                const saved = await savePlaybackPositionPosition(playerError?.code !== NETWORK_ERROR_CODE);

                if (saved === false) {
                    bookmarkFailureCountRef.current += 1;
                } else if (saved) {
                    bookmarkFailureCountRef.current = 0;
                }
            }, config.app_config.player_settings.bookmark_save_interval);
        }

        const playbackEnded = !!playerError || streamEndReached;

        if (playbackEnded && !playbackEndSavedRef.current) {
            playbackEndSavedRef.current = true;
            savePlaybackPositionPosition(playerError?.code !== NETWORK_ERROR_CODE);
        } else if (!playbackEnded) {
            playbackEndSavedRef.current = false;
        }

        if ((!isPlaying || playerError || streamEndReached) && savePlaybackPositionIntervalRef.current) {
            clearInterval(savePlaybackPositionIntervalRef.current);
            savePlaybackPositionIntervalRef.current = null;
        }
    }, [isPlaying, playerError, savePlaybackPositionPosition, streamEndReached]);

    useEffect(() => {
        if (playerError && STREAM_ERRORS.includes(playerError.message)) {
            PlaybackManager.stopOnFatalError();
        }
    }, [playerError]);

    useEffect(() => {
        bookmarkFailureCountRef.current = 0;
        playbackEndSavedRef.current = false;
    }, [asset?.id]);

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                savePlaybackPositionOnUnload();
                return;
            }

            engine.current?.resumeStreaming();
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', savePlaybackPositionOnUnload);

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pagehide', savePlaybackPositionOnUnload);
        };
    }, [savePlaybackPositionOnUnload]);

    useEffect(
        () => () => {
            clearInterval(savePlaybackPositionIntervalRef.current);
        },
        []
    );

    useEffect(() => {
        if (engine.current && audioTrack) {
            try {
                engine.current.setAudioLanguage(audioTrack);
                selectedTrack.current.audio = audioTrack;
            } catch (error) {
                console.error('[Player] Error setting audio language:', error);
            }
        }
    }, [audioTrack]);

    useEffect(() => {
        if (engine.current) {
            engine.current.setTextLanguage(subtitleTrack);
            selectedTrack.current.subtitle = subtitleTrack;
        }
    }, [subtitleTrack]);

    useEffect(() => {
        if (engine.current) {
            engine.current.setBitrateTrack(bitrateTrack);
            selectedTrack.current.bitrate = bitrateTrack;
        }
    }, [bitrateTrack]);

    const lastLoadedAssetIdRef = useRef<string | null>(null);
    const currentAssetRef = useRef<PlayingAsset>(null);

    useEffect(() => {
        currentAssetRef.current = asset;
    }, [asset]);

    useEffect(() => {
        if (asset && playerEngine) {
            setLoaded(true);

            PlaybackManager.setAssetChangedListener((newAsset) => {
                setAsset(newAsset);
            });
            PlaybackManager.setEngine(playerEngine);

            if (asset.id !== lastLoadedAssetIdRef.current) {
                lastLoadedAssetIdRef.current = asset.id;
                PlaybackManager.updatePlayingAsset(asset).catch(() => {});
            } else {
                PlaybackManager.syncAssetMetadata(asset);
            }
        } else if (!asset) {
            lastLoadedAssetIdRef.current = null;
        }
    }, [asset, playerEngine]);

    useEffect(() => {
        castConnectedRef.current = castConnected;

        if (engine.current) {
            initVolumeLevel();
        }

        if (castConnected) {
            castSubtitleIntentRef.current = subtitleTrackRef.current ?? selectedTrack.current.subtitle;
        } else {
            castSubtitleIntentRef.current = null;
            setAudioTrack(null);
            setSubtitleTrack(null);
        }

        setInitCasting(castConnected);
        PlaybackManager.setCastConnected(castConnected, currentAssetRef.current);
    }, [castConnected]);

    useEffect(() => {
        toJumpValueRef.current = toJumpValue;
    }, [toJumpValue]);

    useEffect(() => {
        // necessary reference for the callbacks
        errorRef.current = playerError;
    }, [playerError]);

    useEffect(() => {
        PlaybackManager.setPlayingMode(playingMode);
    }, [playingMode]);

    useEffect(() => {
        if (isLivePlayerAsset(asset)) {
            if (playingMode === PlayingMode.TIME_SHIFT) {
                const url = new URL(window.location.href);
                if (url.searchParams.get('live') === 'true') {
                    url.searchParams.delete('live');
                    navigate({ search: url.search }, { replace: true });
                }
            } else {
                const url = new URL(window.location.href);
                url.searchParams.set('live', 'true');
                navigate({ search: url.search }, { replace: true });
            }
        }
    }, [playingMode]);

    useEffect(() => {
        subtitleTrackRef.current = subtitleTrack;
        audioTrackRef.current = audioTrack;
        bitrateTrackRef.current = bitrateTrack;
    }, [subtitleTrack, audioTrack, bitrateTrack]);

    useEffect(() => {
        attachPlayerListeners();
        return () => detachPlayerListeners();
    }, [attachPlayerListeners, detachPlayerListeners]);

    useEffect(() => {
        engine.current = playerEngine;

        if (playerEngine) {
            PlaybackManager.setEngine(playerEngine);

            PlaybackManager.setAssetChangedListener((newAsset) => {
                setAsset(newAsset);
            });

            PlaybackManager.setCastInitialisedListener(() => {
                setInitCasting(false);
            });

            PlaybackManager.setPlaybackModeChangeListener((mode) => {
                setPlayingMode(mode);
            });

            PlaybackManager.setTakeoverListener(() => {
                setPlayerError({ code: 0, message: STREAM_REPLACED });
            });

            PlaybackManager.setSessionRejectedListener(() => {
                setPlayerError({ code: 0, message: CONTENT_UNAVAILABLE });
            });

            if (engine.current) {
                initVolumeLevel();
                setInitialized(true);
            }
        }
    }, [playerEngine]);

    useEffect(() => {
        window.addEventListener('online', onlineStateChanged);

        return () => {
            window.removeEventListener('online', onlineStateChanged);
        };
    }, [onlineStateChanged]);

    useEffect(() => {
        if (asset && engine.current) {
            const youboraOptions: YouboraOptions = {
                enabled: true,
                'content.title': asset?.title,
                'content.playbackType': YouboraTracking.getPlaybackType(asset, playingMode),
                'content.episodeTitle': String(asset?.episode_title || ''),
                'content.isLive': isLivePlayerAsset(asset),
            };

            if (isLivePlayerAsset(asset) || isCatchupPlayerAsset(asset)) {
                youboraOptions['content.channel'] = asset.channelId;
            }

            if (asset.analytics_npaw) {
                engine.current?.getYoubora()?.setServerAnalytics(asset.analytics_npaw);
            }

            engine.current?.getYoubora()?.setOptions(youboraOptions);
        }
    }, [asset, playingMode]);

    useEffect(() => {
        if (asset && isLivePlayerAsset(asset)) {
            const { channel_id } = asset;
            setLocalStorage('lastPlayedChannel', channel_id);
        }
    }, [asset]);

    useEffect(() => {
        PlaybackManager.setIsReplayCapable(isReplayCapable);
    }, [isReplayCapable]);

    return {
        initialized,
        setEngine,
        asset,
        setAsset,
        release,
        resetPlayerAsset,
        loaded,
        setPlayingMode,
        playingMode,
        engine,
        playerEngine,
        isOpen,
        setIsOpen,
        setStreamEndReached,
        streamEndReached,
        nextEpisode,
        nextEpisodeWatchable,
        displayBingeWatch,
        bingeWatchDuration,
        dismissBingeWatchingBanner,
        setDisplayBingeWatch,
        loadNextEpisode,
        isEmbedded,
        setIsEmbedded,

        getDuration,
        getProgress,
        getSecondaryProgress,
        getProgressWithJump,
        getSecondaryProgressWithJump,
        canUpdateProgress,

        savePlaybackPositionPosition,
        getPrePadding,
        getPostPadding,
        getSeekMax,

        setActiveControlGroup,
        activeControlGroup,

        getTextLanguages,
        getAudioLanguages,
        getBitrateTracks,
        setAudioLanguage,
        setTextLanguage,
        setBitrateStream,
        audioTrack,
        subtitleTrack,
        bitrateTrack,
        hasMultipleTracks,

        isPlaying,
        isBuffering: isBuffering || initCasting,
        volume,
        volumeRef,
        isMuted,
        seekStart,
        seekEnd,
        seek,
        getThumbnail,
        showThumbnail,
        jump,
        adjustVolume,
        VOLUME_STEP,
        isMutedRef,
        toggleMute,
        setPlayingState,
        jumpToLive,
        setVolumeLevel,
        addEventListener,
        removeEventListener,

        playerError,
        setPlayerError,
        resetError,

        switchToMiniPlayer,
        closeMiniPlayer: () => {
            closeMiniPlayer();
            // setStreamEndReached(true);
        },
        updateMiniPlayerProps,
        miniPlayerProps,
        isMini,

        showNextEpisode,
        setShowNextEpisode,

        primaryAudioLanguage,
        primarySubtitlesLanguage,
        accessibilitySound,
        accessibilitySubs,
        changeDefaultAudioLanguage,
        changeDefaultSubtitlesLanguage,
        setAccessibilitySound,
        setAccessibilitySubs,
    };
};

export const PlayerContext = createContext<ReturnType<typeof usePlayerService>>(null);

export const PlayerProvider = ({ children }) => {
    const player = usePlayerService();
    return <PlayerContext.Provider value={player}>{children}</PlayerContext.Provider>;
};

export const usePlayer = () => useContext(PlayerContext);
